/**
 * Selection utility functions for multi-select seat operations
 * Used by admin holds and box office seat selection
 */

import { ComputedSeatPosition, SectionDetailResponse } from '@/types/seating'

export interface BoxSelection {
  startX: number
  startY: number
  endX: number
  endY: number
}

export interface CircleSelection {
  centerX: number
  centerY: number
  radius: number
}

/**
 * Get all FREE seats within a rectangular selection box
 */
export function getSeatsInBox(
  box: BoxSelection,
  seats: ComputedSeatPosition[]
): ComputedSeatPosition[] {
  const minX = Math.min(box.startX, box.endX)
  const maxX = Math.max(box.startX, box.endX)
  const minY = Math.min(box.startY, box.endY)
  const maxY = Math.max(box.startY, box.endY)

  return seats.filter(
    (seat) =>
      seat.absX >= minX &&
      seat.absX <= maxX &&
      seat.absY >= minY &&
      seat.absY <= maxY &&
      seat.status === 'FREE'
  )
}

/**
 * Get all FREE seats within a circular selection area
 */
export function getSeatsInCircle(
  circle: CircleSelection,
  seats: ComputedSeatPosition[]
): ComputedSeatPosition[] {
  return seats.filter((seat) => {
    const distance = Math.sqrt(
      Math.pow(seat.absX - circle.centerX, 2) + Math.pow(seat.absY - circle.centerY, 2)
    )
    return distance <= circle.radius && seat.status === 'FREE'
  })
}

/**
 * Get all FREE seats in a specific row from section data
 */
export function getSeatsInRow(
  sectionData: { seatPositions?: ComputedSeatPosition[] },
  rowLabel: string
): ComputedSeatPosition[] {
  if (!sectionData.seatPositions) return []

  return sectionData.seatPositions.filter(
    (seat) => seat.rowLabel === rowLabel && seat.status === 'FREE'
  )
}

/**
 * Get all FREE seats in a section (for GA/BOOTH sections)
 */
export function getSeatsInSection(sectionData: { seatPositions?: ComputedSeatPosition[] }): ComputedSeatPosition[] {
  if (!sectionData.seatPositions) return []

  return sectionData.seatPositions.filter((seat) => seat.status === 'FREE')
}

/**
 * Convert canvas coordinates to world coordinates
 * (Used for box/circle selection with zoom/pan)
 */
export function canvasToWorld(
  canvasX: number,
  canvasY: number,
  viewTransform: { x: number; y: number; scale: number }
): { x: number; y: number } {
  return {
    x: (canvasX - viewTransform.x) / viewTransform.scale,
    y: (canvasY - viewTransform.y) / viewTransform.scale,
  }
}

/**
 * Get unique row labels from a set of seats
 */
export function getUniqueRowLabels(seats: ComputedSeatPosition[]): string[] {
  const rowLabels = new Set(seats.map((s) => s.rowLabel))
  return Array.from(rowLabels).sort()
}

/**
 * Group seats by section for display purposes
 */
export function groupSeatsBySection(
  seats: ComputedSeatPosition[]
): Map<string, ComputedSeatPosition[]> {
  const groups = new Map<string, ComputedSeatPosition[]>()

  for (const seat of seats) {
    const existing = groups.get(seat.sectionKey) || []
    existing.push(seat)
    groups.set(seat.sectionKey, existing)
  }

  return groups
}

/**
 * Sort seats for display (by section, row, then seat number)
 */
export function sortSeats(seats: ComputedSeatPosition[]): ComputedSeatPosition[] {
  return seats.sort((a, b) => {
    // First by section
    if (a.sectionKey !== b.sectionKey) {
      return a.sectionKey.localeCompare(b.sectionKey)
    }
    // Then by row
    if (a.rowLabel !== b.rowLabel) {
      return a.rowLabel.localeCompare(b.rowLabel)
    }
    // Then by seat number
    return a.seatIndex - b.seatIndex
  })
}

/**
 * Calculate total price for selected seats
 */
export function calculateTotalPrice(seats: ComputedSeatPosition[]): number {
  return seats.reduce((sum, seat) => sum + (seat.price || 0), 0)
}
